import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEffectOnce } from "react-use";
import useLatestRef from "../../react/react-use/useLatestRef";
import {
  Button,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  ThemeProvider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CloseIcon from "@material-ui/icons/Close";

import { useGroopitApiContext, useGroupContext } from "groopit-js";
import {
  AuthorRole,
  ChatWithYourDataConversation,
  ChatWithYourDataEntry,
  ChatWithYourDataErrorResponse,
  ChatWithYourDataResponse,
  DatePickerRangeDTO,
  DatePickerRangeId,
  GroopitFilterAssignmentDisplayDTO,
  StartChatWithYourDataRequest,
} from "../../../groopit-js/client";

import { aiTheme, groopTheme } from "../../styles/MaterialGroopTheme";

import { Ask } from "../../Icons/Ask";
import { CopyIcon } from "../../Icons/CopyIcon";
import { TypingIndicator } from "../../Icons/TypingIndicator";

import { FakeStreamingMarkdown } from "./FakeStreamingMarkdown";
import { StreamingChatWithYourDataLineEntry } from "./StreamingChatWithYourDataLineEntry";
import { useFilterContext } from "../../FeedFilter/State/FilterContext";
import { AssistantPromptRole, UserPromptRole } from "./PromptRoles";
import { FilterSummaryGenerator } from "../../FeedFilter/FilterSummaryGenerator";
import { createFilterReplaceFilterAction } from "../../FeedFilter/State/FilterActions";
import { useFilterDispatchContext } from "../../FeedFilter/State/FilterDispatchContext";
import { UnnamedGroopitFilterDisplayDTO } from "../../FeedFilter/GroopitFilterDTO";
import { GroopitFilterDisplay } from "../../Models/GroopitFilterDisplay";

const MaxTokensExceeded = "MaxTokensExceeded";

const errorMessageForAllOtherErrors =
  "There was an issue processing your input, please try again.";
const TokenLimitExceededForConversationContinuationErrorMessage =
  "You’ve reached AI's limit for this conversation. Close this conversation and start a new one.";
const TokenLimitExceededForConversationStartErrorMessage =
  "You've exceeded AI's limit to ask anything. Reduce the dataset using AI focus settings and try again.";

export const ChatWithYourData = () => {
  const { avatar, group, user_id: userId, enable_chat } = useGroupContext();
  const groopId = group.id;
  const { filterDispatcher } = useFilterDispatchContext();
  const { filter } = useFilterContext();

  const avoidHandlingFilterChangesWhenThisComponentIsTheSourceOfTheChanges =
    useRef(false);

  const api = useLatestRef(useGroopitApiContext());

  const aiConversationRef = useRef<HTMLDivElement | null>(null);
  const inputBaseRef = useRef<HTMLInputElement | null>(null);

  const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);
  const [historicUserQuestions, setHistoricUserQuestions] = useState<
    ChatWithYourDataConversation[]
  >([]);
  const [chatWithYourDataLineEntries, setChatWithYourDataLineEntries] =
    useState<StreamingChatWithYourDataLineEntry[]>([]);
  const [
    currentChatWithYourDataConversation,
    setCurrentChatWithYourDataConversation,
  ] = useState<ChatWithYourDataConversation>();

  const [conversationId, setConversationId] = useState<string>("");

  const [inputText, setInputText] = useState<string>("");
  const [chatWindowExpanded, setChatWindowExpanded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const userAvatarUrl = avatar ?? "/Content/Images/default_avatar.png";

  useEffectOnce(() => {
    async function loadExistingConversations() {
      const loadedConversations =
        await api.current.ai.getChatWithYourDataConversations(groopId);

      if (loadedConversations.length) {
        setHistoricUserQuestions(loadedConversations);
      }
    }
    loadExistingConversations();
  });

  useEffect(() => {
    if (isLoading && aiConversationRef.current) {
      aiConversationRef.current.scrollTop =
        aiConversationRef.current.scrollHeight;
    }
  }, [isLoading]);

  useEffect(() => {
    if (chatWindowExpanded && aiConversationRef.current) {
      aiConversationRef.current.scrollTop =
        aiConversationRef.current.scrollHeight;
    }
  }, [chatWindowExpanded]);

  useEffect(() => {
    if (aiConversationRef.current) {
      aiConversationRef.current.scrollTop =
        aiConversationRef.current.scrollHeight;
    }
  }, [historicUserQuestions]);

  const clearChatMessages = useCallback(() => {
    setChatWithYourDataLineEntries([]);
    setInputText("");
    setConversationId("");
    setChatWindowExpanded(false);
  }, []);

  useEffect(() => {
    if (
      avoidHandlingFilterChangesWhenThisComponentIsTheSourceOfTheChanges.current
    ) {
      avoidHandlingFilterChangesWhenThisComponentIsTheSourceOfTheChanges.current =
        false;
      return;
    }
    clearChatMessages();
    setChatWindowExpanded(false);
  }, [clearChatMessages, filter]);

  // Sends the next part of the conversation from the user. This will be either the start of a conversation by the user
  // or the next piece of the continuation from the user.
  const handleSendNextDialogInConversation = async () => {
    if (!inputText.trim()) {
      return;
    }

    if (!chatWindowExpanded) {
      setChatWindowExpanded(true);
    }

    setIsLoading(true);

    const userChatWithYourDataEntryToSendWithRequest =
      new ChatWithYourDataEntry({
        chatLine: inputText.trim(),
        conversationId: conversationId,
        groopId: groopId,
        role: new AuthorRole({ label: UserPromptRole }),
        timestamp: new Date(),
        userId: userId,
      });

    const userStreamingChatWithYourDataLineEntry =
      new StreamingChatWithYourDataLineEntry({
        chatLine: inputText.trim(),
        conversationId: conversationId,
        groopId: groopId,
        role: new AuthorRole({ label: UserPromptRole }),
        isStreaming: false,
        timestamp: new Date(),
        userId: userId,
      });

    setChatWithYourDataLineEntries((previous) => [
      ...previous,
      userStreamingChatWithYourDataLineEntry,
    ]);
    setInputText("");
    setAwaitingResponse(true);
    if (!chatWithYourDataLineEntries.length) {
      await handleStartConversation(userChatWithYourDataEntryToSendWithRequest);
    } else {
      await handleContinueConversation(
        userChatWithYourDataEntryToSendWithRequest,
      );
    }
  };

  const handleStartConversation = async (
    userChatWithYourDataEntry: ChatWithYourDataEntry,
  ) => {
    const endDate = new Date();

    if (filter.filter.assignments?.length) {
      filter.filter.assignments.forEach((assignment) => {
        if (!assignment.dateRange) {
          assignment.dateRange = new DatePickerRangeDTO();
          assignment.dateRange.endDate = endDate;
          assignment.dateRange.id = DatePickerRangeId.Custom;
        } else {
          assignment.dateRange.endDate = endDate;
          assignment.dateRange.id = DatePickerRangeId.Custom;
        }
      });
    } else if (group.assignments) {
      filter.filter.assignments = group.assignments.map((assignment) => {
        const dateRange = new DatePickerRangeDTO();
        dateRange.endDate = endDate;
        dateRange.id = DatePickerRangeId.Custom;

        const groopitFilterAssignmentDisplay =
          new GroopitFilterAssignmentDisplayDTO();
        groopitFilterAssignmentDisplay.dateRange = dateRange;
        groopitFilterAssignmentDisplay.id = assignment.id;
        groopitFilterAssignmentDisplay.title = assignment.title ?? "";

        return groopitFilterAssignmentDisplay;
      });
    }

    avoidHandlingFilterChangesWhenThisComponentIsTheSourceOfTheChanges.current =
      true;

    filterDispatcher(
      createFilterReplaceFilterAction(
        new UnnamedGroopitFilterDisplayDTO({
          filter: filter.filter,
        }),
      ),
    );

    const startChatWithYourDataRequest = new StartChatWithYourDataRequest({
      chatWithYourDataEntry: userChatWithYourDataEntry,
      conversationTitle: FilterSummaryGenerator(filter.filter),
      filter: GroopitFilterDisplay.convertToGroopitFilter(filter.filter),
    });

    let startChatWithYourDataResponse: ChatWithYourDataResponse;

    try {
      startChatWithYourDataResponse =
        await api.current.ai.startChatWithYourData(
          startChatWithYourDataRequest,
        );

      setConversationId(startChatWithYourDataResponse.conversationId);

      setAwaitingResponse(false);

      const aiResponseStreamingChatWithYourDataLineEntry =
        new StreamingChatWithYourDataLineEntry({
          chatLine: startChatWithYourDataResponse.response,
          conversationId: "",
          groopId: groopId,
          role: new AuthorRole({ label: AssistantPromptRole }),
          isStreaming: true,
          timestamp: new Date(startChatWithYourDataResponse.timestamp),
          userId: userId,
        });
      setChatWithYourDataLineEntries((previous) => [
        ...previous,
        aiResponseStreamingChatWithYourDataLineEntry,
      ]);

      const userConversationStarters =
        await api.current.ai.getChatWithYourDataConversations(group.id);

      const currentConversation = userConversationStarters.find(
        (userConversationStarter) =>
          userConversationStarter.conversationId ===
          startChatWithYourDataResponse.conversationId,
      );

      setCurrentChatWithYourDataConversation(currentConversation);

      setHistoricUserQuestions(userConversationStarters);
      setIsLoading(false);
    } catch (error) {
      const chatWithYourDataLineEntryWithError =
        new StreamingChatWithYourDataLineEntry({
          chatLine: errorMessageForAllOtherErrors,
          conversationId: "",
          groopId: groopId,
          isStreaming: false,
          role: new AuthorRole({ label: MaxTokensExceeded }),
          timestamp: new Date(),
          userId: userId,
        });

      if (error instanceof ChatWithYourDataErrorResponse) {
        if (error.exceededMaximumTokens) {
          chatWithYourDataLineEntryWithError.chatLine =
            TokenLimitExceededForConversationStartErrorMessage;
          chatWithYourDataLineEntryWithError.role = new AuthorRole({
            label: MaxTokensExceeded,
          });
        }
      }

      const previouslyAskedUsedQuestionsResponse =
        await api.current.ai.getChatWithYourDataConversations(group.id);
      setHistoricUserQuestions(previouslyAskedUsedQuestionsResponse);

      setChatWithYourDataLineEntries((previous) => [
        ...previous,
        chatWithYourDataLineEntryWithError,
      ]);

      setIsLoading(false);
      setAwaitingResponse(false);
      return;
    }
  };

  const handleContinueConversation = async (
    userChatWithYourDataEntry: ChatWithYourDataEntry,
  ) => {
    try {
      const response = await api.current.ai.continueChatWithYourData(
        userChatWithYourDataEntry,
      );

      setAwaitingResponse(false);

      setChatWithYourDataLineEntries((previous) => [
        ...previous,
        new StreamingChatWithYourDataLineEntry({
          chatLine: response.response,
          conversationId: conversationId,
          groopId: groopId,
          role: new AuthorRole({ label: AssistantPromptRole }),
          isStreaming: true,
          timestamp: new Date(response.timestamp),
          userId: userId,
        }),
      ]);

      setIsLoading(false);
    } catch (error) {
      if (error instanceof ChatWithYourDataErrorResponse) {
        if (error.exceededMaximumTokens) {
          setChatWithYourDataLineEntries((previous) => [
            ...previous,
            new StreamingChatWithYourDataLineEntry({
              chatLine:
                TokenLimitExceededForConversationContinuationErrorMessage,
              conversationId: "",
              groopId: groopId,
              role: new AuthorRole({ label: MaxTokensExceeded }),
              isStreaming: false,
              timestamp: new Date(),
              userId: userId,
            }),
          ]);

          const userQuestions =
            await api.current.ai.getChatWithYourDataConversations(group.id);
          setHistoricUserQuestions(userQuestions);

          setIsLoading(false);
          setAwaitingResponse(false);

          return;
        }
      }

      setChatWithYourDataLineEntries((previous) => [
        ...previous,
        new StreamingChatWithYourDataLineEntry({
          chatLine: errorMessageForAllOtherErrors,
          conversationId: "",
          groopId: groopId,
          role: new AuthorRole({ label: MaxTokensExceeded }),
          isStreaming: false,
          timestamp: new Date(),
          userId: userId,
        }),
      ]);
      setIsLoading(false);
      setAwaitingResponse(false);
      return;
    }
  };

  const handleConversationMenuOpened = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleConversationMenuClosed = () => {
    setMenuAnchorEl(null);
  };

  const handleConversationSelected = async (conversationId: string) => {
    setChatWindowExpanded(false);
    setMenuAnchorEl(null);
    setIsLoading(true);
    setConversationId(conversationId);
    setChatWithYourDataLineEntries([]);

    const selectedConversation = historicUserQuestions.find(
      (historicUserQuestion) =>
        historicUserQuestion.conversationId === conversationId,
    );

    setCurrentChatWithYourDataConversation(selectedConversation);

    if (selectedConversation?.feedFilter) {
      avoidHandlingFilterChangesWhenThisComponentIsTheSourceOfTheChanges.current =
        true;
      filterDispatcher(
        createFilterReplaceFilterAction(
          new UnnamedGroopitFilterDisplayDTO({
            filter: selectedConversation?.feedFilter,
          }),
        ),
      );
    }

    const conversationLineEntries =
      await api.current.ai.loadConversationLineEntries(conversationId);

    const loadedChatWithYourDataLineEntries = conversationLineEntries
      .sort((a, b) => {
        const timestampDiff =
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        if (timestampDiff !== 0) {
          return timestampDiff;
        }
        // Secondary sorting: "User" role comes before "Assistant" role when the times are the same because
        // the User would have sent the message first.
        if (a.role.label === "User" && b.role.label === "Assistant") {
          return 1;
        }
        if (a.role.label === "Assistant" && b.role.label === "User") {
          return -1;
        }
        return 0;
      })
      .map(
        (chatWithYourDataLineEntry) =>
          new StreamingChatWithYourDataLineEntry({
            chatLine: chatWithYourDataLineEntry.chatLine,
            conversationId: conversationId,
            groopId: groopId,
            isStreaming: false,
            role: chatWithYourDataLineEntry.role,
            timestamp: new Date(chatWithYourDataLineEntry.timestamp),
            userId: userId,
          }),
      );

    setChatWithYourDataLineEntries(loadedChatWithYourDataLineEntries);

    const lastUserMessage = loadedChatWithYourDataLineEntries
      .reverse()
      .find((entry) => entry.role.label === UserPromptRole)?.chatLine;
    setInputText(lastUserMessage ?? "");

    setChatWindowExpanded(true);
    setIsLoading(false);
  };

  const handleCopyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  const handleStreamingFinished = useCallback(() => {
    setIsLoading(false);
    setTimeout(() => {
      if (inputBaseRef.current) {
        inputBaseRef.current.focus();
      }
    }, 0);
  }, []);

  const handleOnTick = useCallback(() => {
    if (aiConversationRef.current) {
      aiConversationRef.current.scrollTop =
        aiConversationRef.current.scrollHeight;
    }
  }, []);

  const useStyles = makeStyles((theme) => ({
    aiConversation: {
      height: chatWindowExpanded ? "500px" : "0",
      marginBottom: chatWindowExpanded ? theme.spacing(4) : 0,
      transition: "height 0.3s ease",
    },
    cwydIconContainer: {
      marginLeft: theme.spacing(1),
    },
    divider: {
      height: 28,
      margin: 4,
    },
    iconButton: {
      padding: 6,
    },
    input: {
      flex: 1,
    },
    paper: {
      borderColor: aiTheme.palette.primary.main,
      borderRadius: 24,
      borderWidth: 2,
      flex: 1,
    },
  }));

  const classes = useStyles();

  return (
    <>
      {enable_chat && (
        <div className="border-app-gray-09 border flex flex-col items-end justify-end p-4 relative w-full">
          <ThemeProvider theme={groopTheme}>
            <div
              ref={aiConversationRef}
              className={`${classes.aiConversation} mt-0 overflow-y-auto relative w-full`}
            >
              {chatWithYourDataLineEntries.map(
                (chatWithYourDataLineEntry, index) => (
                  <div
                    key={index}
                    style={{
                      textAlign: (() => {
                        if (
                          chatWithYourDataLineEntry.role.label ===
                          UserPromptRole
                        )
                          return "right";
                        if (
                          chatWithYourDataLineEntry.role.label ===
                          AssistantPromptRole
                        )
                          return "left";
                        return "left";
                      })(),
                      margin: "5px 0",
                    }}
                  >
                    {(() => {
                      if (
                        chatWithYourDataLineEntry.role.label === UserPromptRole
                      ) {
                        return (
                          <>
                            <strong>
                              <div className="align-items-center flex justify-end text-color-hint">
                                {chatWithYourDataLineEntry.timestamp
                                  ?.toLocaleString("en-US", {
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  })
                                  .replace(" at ", " | ")}
                                <img
                                  alt="User avatar"
                                  className="img-circle avatar-img ml-2"
                                  src={userAvatarUrl}
                                  style={{
                                    borderRadius: "50%",
                                    height: "24px",
                                    width: "24px",
                                  }}
                                  title="avatar"
                                />
                              </div>
                              <div>{chatWithYourDataLineEntry.chatLine}</div>
                            </strong>
                          </>
                        );
                      }
                    })()}
                    {(() => {
                      if (
                        chatWithYourDataLineEntry.role.label ===
                        AssistantPromptRole
                      ) {
                        return (
                          <>
                            <strong>
                              <div className="inline-flex items-center">
                                <img
                                  alt="Groopit AI Conversation Icon"
                                  src="/Content/Images/groop_icon_large.png"
                                  className="align-middle h-6 mr-2 text-color-hint"
                                />
                                <span className="text-color-hint">
                                  {chatWithYourDataLineEntry.timestamp
                                    ?.toLocaleString("en-US", {
                                      day: "numeric",
                                      hour: "numeric",
                                      minute: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                    })
                                    .replace(" at ", " | ")}
                                </span>
                              </div>
                            </strong>
                            <div>
                              {index == 1 ? (
                                <>
                                  <strong>
                                    Responding from{" "}
                                    {currentChatWithYourDataConversation?.conversationTitle ??
                                      ""}
                                  </strong>
                                  <br />
                                  <br />
                                </>
                              ) : null}
                            </div>
                            <div>
                              <FakeStreamingMarkdown
                                content={chatWithYourDataLineEntry.chatLine}
                                onComplete={handleStreamingFinished}
                                onTick={handleOnTick}
                                shouldFakeStreaming={
                                  chatWithYourDataLineEntry.isStreaming
                                }
                              />
                            </div>
                            <div className="flex justify-content-start ml-1 mt-2.5">
                              <div className="box-border cursor-pointer flex 8-8 items-center justify-content-center">
                                <Button
                                  onClick={() =>
                                    handleCopyToClipboard(
                                      chatWithYourDataLineEntry.chatLine,
                                    )
                                  }
                                >
                                  <CopyIcon className="cursor-pointer mr-1 text-sm" />
                                  <span>Copy response</span>
                                </Button>
                                <Button
                                  className="cursor-pointer flex items-center justify-end ml-2 relative"
                                  onClick={clearChatMessages}
                                >
                                  <CloseIcon className="cursor-pointer mr-1 text-sm" />
                                  <span>Close conversation</span>
                                </Button>
                              </div>
                            </div>
                          </>
                        );
                      }
                    })()}
                    {(() => {
                      if (
                        chatWithYourDataLineEntry.role.label ===
                        MaxTokensExceeded
                      ) {
                        return (
                          <>
                            <strong>
                              <div className="inline-flex items-center">
                                <img
                                  alt="Groopit AI Conversation Icon"
                                  src="/Content/Images/groop_icon_large.png"
                                  className="align-middle h-6 mr-2"
                                />
                                <span className="text-color-hint">
                                  {chatWithYourDataLineEntry.timestamp
                                    ?.toLocaleString("en-US", {
                                      day: "numeric",
                                      hour: "numeric",
                                      minute: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                    })
                                    .replace(" at ", " | ")}
                                </span>
                              </div>
                            </strong>
                            <div>{chatWithYourDataLineEntry.chatLine}</div>
                          </>
                        );
                      }
                    })()}
                  </div>
                ),
              )}
              {(() => {
                if (awaitingResponse) {
                  return (
                    <div className="place-self-start">
                      <TypingIndicator />
                    </div>
                  );
                }
              })()}
            </div>
            <div className="flex items-center justify-between mt-auto w-full">
              <Paper
                component="div"
                className={`${classes.paper} items-center flex w-full`}
                variant="outlined"
              >
                <InputBase
                  className={`${classes.input} ml-3 text-color-hint`}
                  disabled={isLoading}
                  inputProps={{ "aria-label": "Chat with your data" }}
                  inputRef={inputBaseRef}
                  onChange={(changeEvent) =>
                    setInputText(changeEvent.target.value)
                  }
                  onKeyDown={(keyboardEvent) => {
                    if (keyboardEvent.key === "Enter") {
                      keyboardEvent.preventDefault();
                      handleSendNextDialogInConversation();
                    }
                  }}
                  placeholder="Ask anything"
                  startAdornment={
                    <InputAdornment position="start">
                      <Ask
                        style={{ color: groopTheme.palette.primary.main }}
                        fontSize="small"
                      />
                    </InputAdornment>
                  }
                  value={inputText}
                />
                <Divider className={classes.divider} orientation="vertical" />
                <IconButton
                  aria-label="Conversation history"
                  className={`${classes.iconButton} cursor-pointer`}
                  disabled={historicUserQuestions.length === 0}
                  onClick={handleConversationMenuOpened}
                >
                  <ArrowDropDownIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  keepMounted
                  open={Boolean(menuAnchorEl)}
                  onClose={handleConversationMenuClosed}
                >
                  {[...historicUserQuestions]
                    .sort(
                      (a, b) =>
                        new Date(b.timestamp).getTime() -
                        new Date(a.timestamp).getTime(),
                    )
                    .map((conversation, index) => (
                      <MenuItem
                        key={index}
                        onClick={() =>
                          handleConversationSelected(
                            conversation.conversationId,
                          )
                        }
                      >
                        {conversation.initialInput}
                      </MenuItem>
                    ))}
                </Menu>
              </Paper>
            </div>
          </ThemeProvider>
        </div>
      )}
    </>
  );
};
